import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowTopRightOnSquareIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { toast } from "react-hot-toast";
import {
  updateUrlWithException,
  InitialsTag,
  dateToString,
} from "../../../components/Utils";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { useNavigate, useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import { getContactsAsync } from "../../../slices/customer/contact";
import ContactDialog from "../../../components/dashboard/contacts/dialog";
import {
  ContactsDataTable,
  getContactColumns,
} from "../../../components/dashboard/contacts/contacts-table";
import { BulkBar } from "../../../components/utils/BulkBar";
import { TableToolbar } from "../../../components/utils/TableToolbar";
import { PMDialog } from "../../../components/utils/PMDialog";
import { useTableView } from "../../../components/utils/PMTableView/useTableView";
import {
  RightDrawer,
  DrawerProps,
  DrawerSection,
} from "../../../components/utils/PMTableView/RightDrawer";

export const ContactList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const itemState = useSelector((store) => store.contacts);
  const { isLoading } = itemState;

  const [searchParams] = useSearchParams();
  const [selectedItems, setSelectedItems] = useState([]);
  const [drawerContact, setDrawerContact] = useState(null);

  // Prevents double-dispatch when handleLoadMore updates controller.page
  const skipNextDispatchRef = useRef(false);

  const [controller, setController] = useState({
    filters: [],
    page: searchParams.get("page") ? parseInt(searchParams.get("page"), 10) : 1,
    per_page: searchParams.get("per_page")
      ? parseInt(searchParams.get("per_page"), 10)
      : 50,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "asc",
    sortBy: searchParams.get("sortBy") ? searchParams.get("sortBy") : "name",
    view: searchParams.get("view") ? searchParams.get("view") : "all",
    fields: "id,name,email,mobile,displayTitle,syncStatus,created_at",
  });

  useEffect(() => {
    updateUrlWithException(
      {
        page: controller.page,
        per_page: controller.per_page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
      },
      "filters"
    );
  }, [controller]);

  // Column definitions + table-view state
  const columns = useMemo(() => getContactColumns(t), [t]);
  const columnMeta = useMemo(
    () =>
      columns.map((c) => ({
        key: c.key,
        label: c.label,
        always: c.always,
        defaultVisible: c.defaultVisible,
        groupable: c.groupable,
      })),
    [columns]
  );
  const groupOptions = useMemo(
    () =>
      columns
        .filter((c) => c.groupable)
        .map((c) => ({ value: c.key, label: c.label })),
    [columns]
  );

  const tableView = useTableView({
    tableKey: "contacts",
    columns: columnMeta,
    getSnapshot: () => ({
      query: controller.query,
      sort: controller.sort,
      sortBy: controller.sortBy,
    }),
    applySnapshot: (snap) => {
      setController((c) => ({
        ...c,
        page: 1,
        query: snap.query || "",
        sort: snap.sort || c.sort,
        sortBy: snap.sortBy || c.sortBy,
      }));
    },
  });

  const tableSort = { field: controller.sortBy, dir: controller.sort };
  const handleSort = (field) => {
    setController((c) => ({
      ...c,
      page: 1,
      sortBy: field,
      sort: c.sortBy === field && c.sort === "asc" ? "desc" : "asc",
    }));
  };

  const handleFiltersChange = (filters) => {
    setController((c) => ({
      ...c,
      page: 1,
      query: filters.query || "",
    }));
  };

  const handleQueryChange = (newQuery) => {
    setController((c) => ({
      ...c,
      page: 1,
      query: newQuery,
    }));
  };

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const filterPanelPlaceholders = useMemo(
    () => [
      { key: "customer", label: t("contact_list.filter.placeholder_customer") },
      { key: "company", label: t("contact_list.filter.placeholder_company") },
      { key: "role", label: t("contact_list.filter.placeholder_role") },
      { key: "tags", label: t("contact_list.filter.placeholder_tags") },
      { key: "sync", label: t("contact_list.filter.placeholder_sync") },
      { key: "status", label: t("contact_list.filter.placeholder_status") },
    ],
    [t]
  );

  // Infinite scroll
  const handleLoadMore = () => {
    const nextPage = (controller.page || 1) + 1;
    skipNextDispatchRef.current = true;
    dispatch(getContactsAsync({ ...controller, page: nextPage, append: true }));
    setController((c) => ({ ...c, page: nextPage }));
  };

  const debouncedDispatchAction = debounce((ctrl) => {
    dispatch(getContactsAsync(ctrl));
  }, 250);

  useEffect(() => {
    if (skipNextDispatchRef.current) {
      skipNextDispatchRef.current = false;
      return;
    }
    debouncedDispatchAction(controller);
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, dispatch]);

  const [contactDialogOpen, setContactDialogOpen] = useState(false);

  const listCount =
    itemState?.totalCount != null ? `${itemState.totalCount}` : null;

  return (
    <>
      <DashboardHeader
        variant="list"
        title={t("Kontakter")}
        listCount={listCount}
        actions={[]}
        mainButton={{
          label: t("New"),
          action: () => setContactDialogOpen(true),
          path: null,
          icon: PlusIcon,
        }}
      >
        <div className="p-3 space-y-2 md:p-5 md:space-y-4">
          {/* Mobile: compact search + collapsed filters */}
          <div className="space-y-2 md:hidden">
            <div className="flex items-center gap-2">
              <div className="relative flex h-9 min-w-0 flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 focus-within:border-slate-400 focus-within:ring-2 focus-within:ring-slate-200">
                <MagnifyingGlassIcon className="h-4 w-4 flex-shrink-0 text-slate-400" />
                <input
                  type="text"
                  value={controller.query}
                  onChange={(e) => handleQueryChange(e.target.value)}
                  placeholder={t("contact_list.search_placeholder")}
                  className="min-w-0 flex-1 border-none bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
                />
                {controller.query ? (
                  <button
                    type="button"
                    onClick={() => handleQueryChange("")}
                    className="flex-shrink-0 text-slate-400 hover:text-slate-600"
                    aria-label={t("Clear")}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(true)}
                className="relative inline-flex h-9 flex-shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50 focus:outline-none"
                aria-label={t("contact_list.filter_title")}
              >
                <FunnelIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Desktop: full filter card */}
          <div className="hidden md:block">
            <ModuleFilter
              filterCard
              disabled={isLoading}
              onFiltersChange={handleFiltersChange}
              initialFilters={{ query: controller.query }}
              searchPlaceholder={t("contact_list.search_placeholder")}
              filterTitle={t("contact_list.filter_title")}
              acceptedStatuses={[]}
            />
          </div>

          {/* Table card */}
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_22px_rgba(0,0,0,0.07)] md:rounded-[18px]">
            <TableToolbar
              groupOptions={groupOptions}
              activeGroup={tableView.groupBy}
              onGroupChange={tableView.setGroupBy}
              columns={columnMeta}
              columnOrder={tableView.order}
              visibleKeys={tableView.visibleKeys}
              onToggleColumn={tableView.toggleColumn}
              onReorderColumn={tableView.reorderColumn}
              onResetColumns={tableView.resetColumns}
              savedViews={tableView.views}
              activeSavedViewId={tableView.activeViewId}
              savedViewDirty={tableView.dirty}
              onApplySavedView={tableView.applyView}
              onSaveAsView={tableView.saveAsView}
              onUpdateView={tableView.updateView}
              onDeleteView={tableView.deleteView}
              density={tableView.density}
              onDensityChange={tableView.setDensity}
            />
            <ContactsDataTable
              columns={columns}
              visibleKeys={tableView.visibleKeys}
              widths={tableView.widths}
              onResize={tableView.resizeColumn}
              groupBy={tableView.groupBy}
              sort={tableSort}
              onSort={handleSort}
              itemState={itemState}
              onItemsSelect={(items) => setSelectedItems(items)}
              density={tableView.density}
              hasMore={
                (itemState?.totalCount || 0) > (itemState?.data?.length || 0)
              }
              onLoadMore={handleLoadMore}
              onRowClick={(contact) => setDrawerContact(contact)}
            />
          </div>
        </div>
      </DashboardHeader>

      {/* Floating bulk action bar */}
      <BulkBar
        count={selectedItems.length}
        onClear={() => setSelectedItems([])}
        actions={[
          {
            label: t("Edit"),
            icon: PencilSquareIcon,
            action: () => toast.error("Kommer snart"),
          },
          { separator: true },
          {
            label: t("Delete"),
            icon: TrashIcon,
            danger: true,
            action: () => toast.error("Kommer snart"),
          },
        ]}
      />

      {/* Row detail pull-out drawer */}
      <RightDrawer
        open={!!drawerContact}
        onClose={() => setDrawerContact(null)}
        eyebrow={t("Contact")}
        title={drawerContact?.name || drawerContact?.displayTitle || t("Contact")}
        actions={[
          {
            label: t("Open"),
            icon: ArrowTopRightOnSquareIcon,
            primary: true,
            onClick: () => navigate(`/dashboard/contacts/${drawerContact.id}`),
          },
        ]}
      >
        {drawerContact && (
          <DrawerSection>
            <DrawerProps
              items={[
                { label: t("Email"), value: drawerContact.email || "—" },
                { label: t("Mobile"), value: drawerContact.mobile || "—" },
                {
                  label: t("Created"),
                  value: drawerContact.created_at
                    ? dateToString(drawerContact.created_at)
                    : "—",
                },
              ]}
            />
          </DrawerSection>
        )}
      </RightDrawer>

      <ContactDialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        readOnly={false}
        redirect={true}
      />

      <PMDialog
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        title={t("contact_list.filter_title")}
        size="md"
        dense
        closeOnOutsideClick
      >
        <div className="overflow-y-auto overscroll-contain px-3 pb-3 sm:px-4 sm:pb-4">
          <ModuleFilter
            panelMode
            hideSearch
            disabled={isLoading}
            onFiltersChange={handleFiltersChange}
            initialFilters={{ query: controller.query }}
            acceptedStatuses={[]}
            panelPlaceholderSlots={filterPanelPlaceholders}
          />
        </div>
      </PMDialog>
    </>
  );
};
